/**
 * Hard-stop blocker popup for answers that disqualify the patient
 * (e.g. under-18 date of birth). X and CTA both dismiss and leave the
 * user on the same question so they can correct the answer.
 *
 * Figma: Weight-Loss / 6170:21680
 *   header  6170:21681
 *   body    6170:21688
 *   footer  6170:21712
 */

import React from "react";
import PortaledModal from "../../../components/Modals/PortaledModal";
import * as translator from "../../../utils/translator";
import { useShellIdFn } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

interface BlockerModalProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
}

const BlockerModal: React.FC<BlockerModalProps> = ({ isOpen, onClose, language }) => {
  const scopeId = useShellIdFn();
  if (!isOpen) return null;

  return (
    <PortaledModal
      shouldShowModal={isOpen}
      hideModal={onClose}
      shouldBackgroundClickHideModal={false}
      backgroundColor="rgba(8, 8, 10, 0.45)"
    >
      {/*
        Mobile: full-bleed bottom sheet, top corners 24px (Figma 375-wide frame).
        Desktop/lg: centered card max 375px to keep the same proportions.
      */}
      <div
        id={scopeId("wl2_blocker_modal")}
        className="
          w-full max-w-[375px] bg-white overflow-hidden
          shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]
          fixed left-0 right-0 bottom-0 mx-auto rounded-t-[24px]
          lg:relative lg:left-auto lg:right-auto lg:bottom-auto lg:rounded-[24px]
        "
      >
        {/* Header — 6170:21681
            pt-16 pb-17 pl-24 pr-16, border-b #f3f4f6
            title Poppins Medium 20 / 1.3 #08080a
            close 36×36 hit area, 20×20 icon */}
        <div className="flex items-start justify-between pt-4 pb-[17px] pl-6 pr-4 border-b border-solid border-[#f3f4f6]">
          <h2 className="font-['Poppins'] font-medium text-[20px] leading-[1.3] text-[#08080a] m-0 max-w-[274px] pr-2">
            {t(language, "blocker_modal_title")}
          </h2>
          <button
            id={scopeId("wl2_blocker_modal_close")}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 size-9 rounded-[10px] flex items-start justify-center pt-2 cursor-pointer hover:opacity-70 active:scale-95 transition"
            style={{ border: "none", background: "transparent", padding: 0 }}
          >
            {/* 20×20 icon box with 10×10 X strokes, matching Figma inset-1/4 vectors */}
            <span className="relative block size-5" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="block">
                <path
                  d="M6 6l8 8M14 6l-8 8"
                  stroke="#08080a"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        </div>

        {/* Body — 6170:21688
            p-20, gap-20
            body Roboto 14 / 16.8 #8c94a3
            notice card bg #f5f6f7, radius 11, p-16, text center #616975 */}
        <div className="flex flex-col gap-5 p-5">
          <p id={scopeId("wl2_blocker_modal_body")} className="font-['Roboto'] font-normal text-[14px] leading-[16.8px] text-[#8c94a3] m-0">
            {t(language, "blocker_modal_body")}
          </p>

          <div className="bg-[#f5f6f7] rounded-[11px] p-4 flex items-center justify-center">
            <p className="font-['Roboto'] font-normal text-[14px] leading-[16.8px] text-[#616975] text-center m-0">
              {t(language, "blocker_modal_notice")}
            </p>
          </div>
        </div>

        {/* Footer — 6170:21712
            border-t #f3f4f6, pt-17 px-24, h-81 in Figma (= 17 + 48 + ~16 bottom)
            CTA h-48 radius-14 gradient #f59200 → #ffbb29
            label Roboto Medium 16 / 24 white */}
        <div className="border-t border-solid border-[#f3f4f6] pt-[17px] px-6 pb-4">
          <button
            id={scopeId("wl2_blocker_modal_cta")}
            type="button"
            onClick={onClose}
            className="
              w-full h-12 rounded-[14px]
              font-['Roboto'] font-medium text-[16px] leading-6 text-white text-center
              flex items-center justify-center
              cursor-pointer hover:opacity-90 active:scale-[0.98] transition
            "
            style={{
              border: "none",
              backgroundImage: "linear-gradient(90deg, #f59200 0%, #ffbb29 100%)",
            }}
          >
            {t(language, "blocker_modal_cta")}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default BlockerModal;
